import { useEffect, useState } from 'react';

/**
 * Tracks whether an externally-owned widget's modal is currently open, based
 * on a pair of document-scoped events the widget dispatches from its own
 * CONFIRMED visibility state (never inferred from our own request to open
 * it — a dispatched open request can be silently dropped if the widget's
 * listener hasn't attached yet, which would leave scroll locked with nothing
 * left to ever unlock it).
 *
 * @param {string} openedEvent - event name dispatched when the modal opens
 * @param {string} closedEvent - event name dispatched when the modal closes
 * @returns {boolean}
 */
const useExternalModalOpen = (openedEvent, closedEvent) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	useEffect(() => {
		const handleOpened = () => setIsModalOpen(true);
		const handleClosed = () => setIsModalOpen(false);

		document.addEventListener(openedEvent, handleOpened);
		document.addEventListener(closedEvent, handleClosed);

		return () => {
			document.removeEventListener(openedEvent, handleOpened);
			document.removeEventListener(closedEvent, handleClosed);
		};
	}, [openedEvent, closedEvent]);

	return isModalOpen;
};

export default useExternalModalOpen;
